import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { queryKeys } from '@/libs/queryKeys';
import { ACCOUNT_BILLING_CREDITS_PATH, ACCOUNT_BILLING_MEMBERSHIP_PATH } from '@/routes/paths';
import { billingService } from '@/services/billingService';
import { useUiStore } from '@/store/uiStore';

const CAPABILITY_LABELS = {
  TEXT: 'text conversations',
  VOICE: 'voice messages',
  VIDEO: 'video responses',
} as const;

/** Recovery dialog shown only after the backend denies a paid capability. */
export const NoCreditsModal: React.FC = () => {
  const isOpen = useUiStore((state) => state.modals.noCredits);
  const recovery = useUiStore((state) => state.billingRecovery);
  const closeModal = useUiStore((state) => state.closeModal);
  const navigate = useNavigate();
  const optionsQuery = useQuery({
    queryKey: queryKeys.billing.options,
    queryFn: billingService.getOptions,
    enabled: isOpen,
    staleTime: 60_000,
  });
  const currentQuery = useQuery({
    queryKey: queryKeys.billing.current,
    queryFn: billingService.getCurrentSubscription,
    enabled: isOpen,
  });

  const goTo = (path: string) => {
    navigate(path);
    closeModal('noCredits');
  };

  const options = optionsQuery.data;
  const eligiblePlans =
    options?.subscription_options.filter(
      (plan) =>
        !recovery?.capability ||
        plan.benefits.some((benefit) => benefit.code === recovery.capability && benefit.included)
    ) ?? [];
  const creditsAvailable = Boolean(
    options?.pay_as_you_go_enabled && options.credit_packages.length > 0
  );
  const membershipAvailable = Boolean(
    !currentQuery.data && !currentQuery.isError && eligiblePlans.length > 0
  );
  const capability = recovery?.capability ? CAPABILITY_LABELS[recovery.capability] : 'this action';
  const isLoading = optionsQuery.isLoading || currentQuery.isLoading;
  const isError = optionsQuery.isError || (currentQuery.isError && !creditsAvailable);

  let title = 'Payment options unavailable';
  let message = `There are no new payment options available for ${capability} right now.`;
  if (creditsAvailable && membershipAvailable) {
    title = 'Choose how to continue';
    message = `Buy credits or activate a membership that includes ${capability}.`;
  } else if (creditsAvailable) {
    title = 'Not enough credits';
    message = `Buy credits to continue using ${capability}.`;
  } else if (membershipAvailable) {
    title = 'Membership required';
    message = `Activate a membership that includes ${capability} to continue.`;
  }

  return (
    <Modal isOpen={isOpen} onClose={() => closeModal('noCredits')} className='max-w-[420px]'>
      <div className='flex flex-col items-center text-center'>
        {isLoading ? (
          <div aria-live='polite' aria-busy='true'>
            <h2 className='font-sans text-24 font-bold text-dark-300'>Checking payment options…</h2>
            <p className='mt-3 font-sans text-15 leading-24 text-dark-200'>
              We’re finding the available ways to continue.
            </p>
          </div>
        ) : isError ? (
          <>
            <h2 className='font-sans text-24 font-bold text-dark-300'>Options did not load</h2>
            <p className='mt-3 font-sans text-15 leading-24 text-dark-200'>
              Check your connection and try again. No purchase has been started.
            </p>
            <Button
              type='button'
              variant='create-echo'
              className='mt-6 min-h-11 w-full text-15 font-bold text-brand-800'
              onClick={() => {
                void optionsQuery.refetch();
                void currentQuery.refetch();
              }}
            >
              Try again
            </Button>
          </>
        ) : (
          <>
            <h2 className='font-sans text-24 font-bold text-dark-300'>{title}</h2>
            <p className='mt-3 font-sans text-15 leading-24 text-dark-200'>{message}</p>
            <div className='mt-6 flex w-full flex-col gap-3'>
              {creditsAvailable && (
                <Button
                  type='button'
                  variant='purple-dark'
                  className='min-h-11 w-full text-15 font-bold text-white'
                  onClick={() => goTo(ACCOUNT_BILLING_CREDITS_PATH)}
                >
                  Buy credits
                </Button>
              )}
              {membershipAvailable && (
                <Button
                  type='button'
                  variant='create-echo'
                  className='min-h-11 w-full text-15 font-bold text-brand-800'
                  onClick={() => goTo(ACCOUNT_BILLING_MEMBERSHIP_PATH)}
                >
                  View memberships
                </Button>
              )}
              {!creditsAvailable && !membershipAvailable && (
                <Button
                  type='button'
                  variant='create-echo'
                  className='min-h-11 w-full text-15 font-bold text-brand-800'
                  onClick={() => closeModal('noCredits')}
                >
                  Close
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};
